/**
 * Sentry — observabilidade de erros do front-end.
 *
 * Inicializacao no entry point (main.tsx). Se VITE_SENTRY_DSN estiver vazio
 * o init é um no-op (úteis para dev local sem ruido).
 *
 * Tags importantes:
 *   - tenant: cod_tenant resolvido pelo subdominio (definido no AuthContext após login)
 *   - role: app_metadata.role do usuário logado
 *
 * Ver `setSentryUser()` para anexar essas tags ao escopo após login.
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.MODE ?? 'development') as string;
const RELEASE = (import.meta.env.VITE_RELEASE ?? 'dev') as string;

export function initSentry(): void {
  if (!DSN) {
    if (ENV !== 'test') {
      console.info('[sentry] VITE_SENTRY_DSN ausente — telemetria desativada');
    }
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: ENV === 'production' ? 0.1 : 0.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event) {
      // Remove PII básica do extra/breadcrumbs
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
      }
      return event;
    },
    ignoreErrors: [
      // Erros conhecidos sem ação possivel
      'ResizeObserver loop limit exceeded',
      'Network request failed',
    ],
  });
}

export function setSentryUser(params: {
  cod_auth_user: string;
  cod_tenant: string;
  role: string;
}): void {
  if (!DSN) return;
  Sentry.setUser({ id: params.cod_auth_user });
  Sentry.setTag('tenant', params.cod_tenant);
  Sentry.setTag('role', params.role);
}

export function clearSentryUser(): void {
  if (!DSN) return;
  Sentry.setUser(null);
  Sentry.setTag('tenant', undefined);
  Sentry.setTag('role', undefined);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
